// src/api/estadoRecepcion.js - API para manejo de estados de recepción
import { apiFetch } from './index.js';

/**
 * Obtener todos los estados de recepción
 * @param {boolean} soloActivos - Si solo obtener estados activos
 * @returns {Promise} Lista de estados de recepción
 */
export const obtenerEstadosRecepcion = async (soloActivos = false) => {
  try {
    console.log('📋 Obteniendo estados de recepción...', { soloActivos });
    
    let endpoint = '/api/EstadoRecepcion';
    
    if (soloActivos) {
      endpoint = '/api/EstadoRecepcion/activos';
    }
    
    const response = await apiFetch(endpoint);
    
    // Normalizar los datos para el frontend
    const data = Array.isArray(response) ? response : (response.data || []);
    const estadosNormalizados = data.map(estado => ({
      idEstadoRecepcion: estado.idEstadoRecepcion,
      nombre: estado.nombre,
      descripcion: estado.descripcion,
      orden: estado.orden || 0,
      color: estado.color,
      estado: estado.estado,
      fechaCreacion: estado.fecha_creacion || estado.fechaCreacion,
      fecha_creacion: estado.fecha_creacion,
      totalRecepciones: estado.totalRecepciones || 0
    }));
    
    return {
      success: true,
      data: estadosNormalizados,
      count: estadosNormalizados.length,
      message: 'Estados de recepción obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo estados de recepción:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener estados de recepción ordenados
 * @returns {Promise} Lista de estados ordenados
 */
export const obtenerEstadosRecepcionOrdenados = async () => {
  try {
    console.log('📋 Obteniendo estados de recepción ordenados...');
    
    const response = await apiFetch('/api/EstadoRecepcion/ordenados');
    
    const data = Array.isArray(response) ? response : (response.data || []);
    const estadosNormalizados = data.map(estado => ({
      idEstadoRecepcion: estado.idEstadoRecepcion,
      nombre: estado.nombre,
      descripcion: estado.descripcion,
      orden: estado.orden || 0,
      color: estado.color,
      estado: estado.estado,
      fechaCreacion: estado.fecha_creacion || estado.fechaCreacion
    }));
    
    return {
      success: true,
      data: estadosNormalizados,
      count: estadosNormalizados.length,
      message: 'Estados ordenados obtenidos exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error obteniendo estados ordenados:', error);
    return {
      success: false,
      error: error.message,
      data: [],
      count: 0
    };
  }
};

/**
 * Obtener un estado de recepción por ID
 * @param {number} id - ID del estado
 * @returns {Promise} Datos del estado
 */
export const obtenerEstadoRecepcionPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de estado es requerido');
    }
    
    console.log(`🔍 Obteniendo estado de recepción ID: ${id}`);
    
    const response = await apiFetch(`/api/EstadoRecepcion/${id}`);
    
    const data = response.data || response;
    
    return {
      success: true,
      data: {
        idEstadoRecepcion: data.idEstadoRecepcion,
        nombre: data.nombre,
        descripcion: data.descripcion,
        orden: data.orden || 0,
        color: data.color,
        estado: data.estado,
        fechaCreacion: data.fecha_creacion,
        fecha_creacion: data.fecha_creacion
      },
      message: response.message || 'Estado obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo estado ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Obtener estado de recepción con relaciones
 * @param {number} id - ID del estado
 * @returns {Promise} Datos del estado con relaciones
 */
export const obtenerEstadoRecepcionConRelaciones = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de estado es requerido');
    }
    
    console.log(`🔍 Obteniendo estado con relaciones ID: ${id}`);
    
    const response = await apiFetch(`/api/EstadoRecepcion/${id}/conRelaciones`);
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estado obtenido exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error obteniendo estado con relaciones ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Verificar si un estado de recepción existe por nombre
 * @param {string} nombre - Nombre a verificar
 * @returns {Promise} Resultado de la verificación
 */
export const verificarEstadoRecepcionPorNombre = async (nombre) => {
  try {
    if (!nombre || nombre.trim() === '') {
      throw new Error('Nombre es requerido');
    }
    
    console.log(`🔎 Verificando estado por nombre: "${nombre}"`);
    
    const response = await apiFetch(`/api/EstadoRecepcion/nombre-exists/${encodeURIComponent(nombre)}`);
    
    return {
      success: true,
      existe: response.exists || response.data?.exists || false,
      message: response.message || (response.exists ? 'El estado ya existe' : 'Estado disponible')
    };
    
  } catch (error) {
    console.error(`❌ Error verificando estado "${nombre}":`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Verificar si un estado de recepción existe por ID
 * @param {number} id - ID del estado
 * @returns {Promise} Resultado de la verificación
 */
export const verificarEstadoRecepcionPorId = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de estado es requerido');
    }
    
    console.log(`🔎 Verificando estado ID: ${id}`);
    
    const response = await apiFetch(`/api/EstadoRecepcion/${id}/exists`);
    
    return {
      success: true,
      existe: response.exists || response.data?.exists || false,
      message: response.message || (response.exists ? 'El estado existe' : 'Estado no existe')
    };
    
  } catch (error) {
    console.error(`❌ Error verificando estado ${id}:`, error);
    return {
      success: false,
      error: error.message,
      existe: false
    };
  }
};

/**
 * Crear un nuevo estado de recepción
 * @param {Object} estadoData - Datos del estado
 * @returns {Promise} Estado creado
 */
export const crearEstadoRecepcion = async (estadoData) => {
  try {
    // Validar datos requeridos
    if (!estadoData.nombre || estadoData.nombre.trim() === '') {
      throw new Error('El nombre del estado es requerido');
    }
    
    console.log('➕ Creando nuevo estado de recepción:', estadoData);
    
    const response = await apiFetch('/api/EstadoRecepcion', {
      method: 'POST',
      body: JSON.stringify({
        nombre: estadoData.nombre.trim(),
        descripcion: estadoData.descripcion || null,
        orden: parseInt(estadoData.orden) || 0,
        color: estadoData.color || 'secondary',
        estado: estadoData.estado || 'activo'
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estado creado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error creando estado:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Actualizar un estado de recepción existente
 * @param {number} id - ID del estado
 * @param {Object} estadoData - Datos a actualizar
 * @returns {Promise} Estado actualizado
 */
export const actualizarEstadoRecepcion = async (id, estadoData) => {
  try {
    if (!id) {
      throw new Error('ID de estado es requerido');
    }
    
    if (!estadoData.nombre || estadoData.nombre.trim() === '') {
      throw new Error('El nombre del estado es requerido');
    }
    
    console.log(`✏️ Actualizando estado ID: ${id}`, estadoData);
    
    const payload = {
      idEstadoRecepcion: parseInt(id),
      nombre: estadoData.nombre.trim(),
      descripcion: estadoData.descripcion || null,
      orden: parseInt(estadoData.orden) || 0,
      color: estadoData.color || 'secondary',
      estado: estadoData.estado || 'activo'
    };
    
    console.log('📦 Payload enviado a la API:', payload);
    
    const response = await apiFetch(`/api/EstadoRecepcion/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estado actualizado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error actualizando estado ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Eliminar un estado de recepción
 * @param {number} id - ID del estado a eliminar
 * @returns {Promise} Resultado de la eliminación
 */
export const eliminarEstadoRecepcion = async (id) => {
  try {
    if (!id) {
      throw new Error('ID de estado es requerido');
    }
    
    console.log(`🗑️ Eliminando estado ID: ${id}`);
    
    const response = await apiFetch(`/api/EstadoRecepcion/${id}`, {
      method: 'DELETE'
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estado eliminado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error eliminando estado ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Cambiar estado (activo/inactivo) de un estado de recepción
 * @param {number} id - ID del estado
 * @param {string} nuevoEstado - Nuevo estado ('activo' o 'inactivo')
 * @returns {Promise} Resultado del cambio
 */
export const cambiarEstadoActivo = async (id, nuevoEstado) => {
  try {
    if (!id || !nuevoEstado) {
      throw new Error('ID y estado son requeridos');
    }
    
    console.log(`🔄 Cambiando estado activo de ${id} a ${nuevoEstado}`);
    
    const response = await apiFetch('/api/EstadoRecepcion/cambiar-estado', {
      method: 'PATCH',
      body: JSON.stringify({
        id: parseInt(id),
        estado: nuevoEstado
      })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Estado cambiado exitosamente'
    };
    
  } catch (error) {
    console.error(`❌ Error cambiando estado de ${id}:`, error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * Activar un estado de recepción
 * @param {number} id - ID del estado
 * @returns {Promise} Resultado de la activación
 */
export const activarEstadoRecepcion = async (id) => {
  return await cambiarEstadoActivo(id, 'activo');
};

/**
 * Desactivar un estado de recepción
 * @param {number} id - ID del estado
 * @returns {Promise} Resultado de la desactivación
 */
export const desactivarEstadoRecepcion = async (id) => {
  return await cambiarEstadoActivo(id, 'inactivo');
};

/**
 * Ordenar estados de recepción
 * @param {Array} ordenes - Array con { id, orden }
 * @returns {Promise} Resultado del ordenamiento
 */
export const ordenarEstadosRecepcion = async (ordenes) => {
  try {
    if (!ordenes || ordenes.length === 0) {
      throw new Error('La lista de órdenes es requerida');
    }
    
    console.log('🔄 Ordenando estados de recepción:', ordenes);
    
    const response = await apiFetch('/api/EstadoRecepcion/reordenar', {
      method: 'POST',
      body: JSON.stringify({ ordenes })
    });
    
    return {
      success: true,
      data: response.data || response,
      message: response.message || 'Orden actualizado exitosamente'
    };
    
  } catch (error) {
    console.error('❌ Error ordenando estados:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== FUNCIONES AUXILIARES (CLIENTE-SIDE) ====================

/**
 * Buscar estados por término (cliente-side)
 * @param {Array} estados - Lista de estados
 * @param {string} termino - Término de búsqueda
 * @returns {Array} Estados filtrados
 */
export const buscarEstadosLocal = (estados, termino) => {
  if (!termino || termino.trim() === '') {
    return estados;
  }
  
  const terminoLower = termino.toLowerCase();
  return estados.filter(estado => 
    estado.nombre?.toLowerCase().includes(terminoLower) ||
    estado.descripcion?.toLowerCase().includes(terminoLower) ||
    estado.color?.toLowerCase().includes(terminoLower)
  );
};

/**
 * Filtrar estados por estado activo (cliente-side)
 * @param {Array} estados - Lista de estados
 * @param {string} filtroEstado - 'all', 'activos', 'inactivos'
 * @returns {Array} Estados filtrados
 */
export const filtrarEstadosPorEstadoLocal = (estados, filtroEstado) => {
  if (filtroEstado === 'all') return estados;
  if (filtroEstado === 'activos') return estados.filter(e => e.estado?.toLowerCase() === 'activo');
  if (filtroEstado === 'inactivos') return estados.filter(e => e.estado?.toLowerCase() === 'inactivo');
  return estados;
};

/**
 * Obtener estadísticas de estados de recepción
 * @param {Array} estados - Lista de estados
 * @returns {Object} Estadísticas
 */
export const obtenerEstadisticasEstadosRecepcion = (estados) => {
  const activos = estados.filter(e => e.estado?.toLowerCase() === 'activo').length;
  const inactivos = estados.filter(e => e.estado?.toLowerCase() === 'inactivo').length;
  
  // Estados con descripción
  const conDescripcion = estados.filter(e => e.descripcion && e.descripcion.trim() !== '').length;
  
  // Distribución por colores
  const colores = {};
  estados.forEach(estado => {
    const color = estado.color || 'default';
    colores[color] = (colores[color] || 0) + 1;
  });
  
  // Rango de órdenes
  const ordenes = estados.filter(e => e.orden !== undefined).map(e => e.orden);
  const minOrden = ordenes.length > 0 ? Math.min(...ordenes) : 0;
  const maxOrden = ordenes.length > 0 ? Math.max(...ordenes) : 0;
  
  // Estados recientes (últimos 7 días)
  const sieteDiasAtras = new Date();
  sieteDiasAtras.setDate(sieteDiasAtras.getDate() - 7);
  const recientes = estados.filter(e => {
    try {
      const fechaCreacion = new Date(e.fecha_creacion || e.fechaCreacion || 0);
      return fechaCreacion >= sieteDiasAtras;
    } catch {
      return false;
    }
  }).length;
  
  return {
    total: estados.length,
    activos,
    inactivos,
    porcentajeActivos: estados.length > 0 ? Math.round((activos / estados.length) * 100) : 0,
    porcentajeInactivos: estados.length > 0 ? Math.round((inactivos / estados.length) * 100) : 0,
    conDescripcion,
    porcentajeConDescripcion: estados.length > 0 ? Math.round((conDescripcion / estados.length) * 100) : 0,
    colores,
    minOrden,
    maxOrden,
    recientes,
    porcentajeRecientes: estados.length > 0 ? Math.round((recientes / estados.length) * 100) : 0
  };
};

/**
 * Ordenar estados por diferentes criterios
 * @param {Array} estados - Lista de estados
 * @param {string} criterio - Criterio de ordenamiento
 * @returns {Array} Estados ordenados
 */
export const ordenarEstados = (estados, criterio = 'orden') => {
  const estadosCopy = [...estados];
  
  switch (criterio) {
    case 'orden':
      return estadosCopy.sort((a, b) => (a.orden || 0) - (b.orden || 0));
    case 'orden_desc':
      return estadosCopy.sort((a, b) => (b.orden || 0) - (a.orden || 0));
    case 'nombre':
      return estadosCopy.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    case 'nombre_desc':
      return estadosCopy.sort((a, b) => (b.nombre || '').localeCompare(a.nombre || ''));
    case 'fechaCreacion_desc':
      return estadosCopy.sort((a, b) => new Date(b.fecha_creacion || b.fechaCreacion || 0) - new Date(a.fecha_creacion || a.fechaCreacion || 0));
    case 'fechaCreacion':
      return estadosCopy.sort((a, b) => new Date(a.fecha_creacion || a.fechaCreacion || 0) - new Date(b.fecha_creacion || b.fechaCreacion || 0));
    case 'color':
      return estadosCopy.sort((a, b) => (a.color || '').localeCompare(b.color || ''));
    case 'estado':
      return estadosCopy.sort((a, b) => (a.estado || '').localeCompare(b.estado || ''));
    default:
      return estadosCopy;
  }
};

/**
 * Formatear fecha de estado
 * @param {string} fechaISO - Fecha en formato ISO
 * @param {boolean} completo - Si incluir hora completa
 * @returns {string} Fecha formateada
 */
export const formatearFecha = (fechaISO, completo = false) => {
  if (!fechaISO) return 'N/A';
  
  try {
    const fecha = new Date(fechaISO);
    const opciones = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    
    if (completo) {
      opciones.hour = '2-digit';
      opciones.minute = '2-digit';
    }
    
    return fecha.toLocaleDateString('es-ES', opciones);
  } catch (error) {
    return fechaISO;
  }
};

/**
 * Obtener clase de color para Bootstrap
 * @param {string} color - Color del estado
 * @returns {string} Clase CSS de Bootstrap
 */
export const obtenerClaseColor = (color) => {
  const coloresMap = {
    'success': 'green',
    'warning': 'amber',
    'danger': 'red',
    'info': 'cyan',
    'primary': 'blue',
    'secondary': 'gray',
    'dark': 'gray',
    'light': 'gray',
    // Nuevos colores
    'indigo': 'indigo',
    'purple': 'purple',
    'pink': 'pink',
    'orange': 'orange',
    'teal': 'teal',
    'fuchsia': 'fuchsia',
    'lime': 'lime',
    'emerald': 'emerald',
    'rose': 'rose',
    'cyan': 'cyan',
    'sky': 'sky',
    'violet': 'violet'
  };
  
  const colorKey = color?.toLowerCase() || 'secondary';
  return coloresMap[colorKey] || colorKey;
};

/**
 * Convertir nombre a texto legible
 * @param {string} nombre - Nombre del estado
 * @returns {string} Nombre formateado
 */
export const formatearNombre = (nombre) => {
  if (!nombre) return '';
  return nombre
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Formatear descripción (cortar si es muy larga)
 * @param {string} descripcion - Descripción del estado
 * @param {number} maxLength - Longitud máxima
 * @returns {string} Descripción formateada
 */
export const formatearDescripcion = (descripcion, maxLength = 100) => {
  if (!descripcion) return 'Sin descripción';
  if (descripcion.length <= maxLength) return descripcion;
  return descripcion.substring(0, maxLength) + '...';
};

/**
 * Obtener etiqueta HTML para estado
 * @param {Object} estado - Estado de recepción
 * @returns {string} HTML de la etiqueta
 */
export const obtenerEtiquetaEstado = (estado) => {
  if (!estado) return '<span class="badge bg-secondary">N/A</span>';
  
  const colorClass = obtenerClaseColor(estado.color);
  const nombre = formatearNombre(estado.nombre);
  const estadoActivo = estado.estado?.toLowerCase() === 'activo';
  
  return `<span class="badge bg-${colorClass} ${!estadoActivo ? 'opacity-50' : ''}">${nombre}</span>`;
};

/**
 * Obtener el siguiente estado según el orden
 * @param {Array} estados - Lista de estados ordenados
 * @param {number} ordenActual - Orden actual
 * @returns {Object|null} Siguiente estado o null
 */
export const obtenerSiguienteEstado = (estados, ordenActual) => {
  const estadosOrdenados = ordenarEstados(estados, 'orden');
  const indexActual = estadosOrdenados.findIndex(e => e.orden === ordenActual);
  if (indexActual !== -1 && indexActual < estadosOrdenados.length - 1) {
    return estadosOrdenados[indexActual + 1];
  }
  return null;
};

/**
 * Obtener el estado anterior según el orden
 * @param {Array} estados - Lista de estados ordenados
 * @param {number} ordenActual - Orden actual
 * @returns {Object|null} Estado anterior o null
 */
export const obtenerEstadoAnterior = (estados, ordenActual) => {
  const estadosOrdenados = ordenarEstados(estados, 'orden');
  const indexActual = estadosOrdenados.findIndex(e => e.orden === ordenActual);
  if (indexActual > 0) {
    return estadosOrdenados[indexActual - 1];
  }
  return null;
};

// ==================== FUNCIONES DE EXPORTACIÓN ====================

/**
 * Exportar estados a Excel
 * @param {Array} estados - Lista de estados
 * @param {string} filename - Nombre del archivo (sin extensión)
 * @returns {Object} Resultado de la exportación
 */
export const exportarExcel = (estados, filename = 'estados_recepcion') => {
  try {
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    const XLSX = require('xlsx');
    const wb = XLSX.utils.book_new();
    
    const data = estados.map(estado => ({
      'ID': estado.idEstadoRecepcion || '',
      'Nombre': formatearNombre(estado.nombre),
      'Descripción': estado.descripcion || '',
      'Orden': estado.orden || 0,
      'Color': estado.color || '',
      'Estado': estado.estado === 'activo' ? 'Activo' : 'Inactivo',
      'Fecha Creación': formatearFecha(estado.fecha_creacion || estado.fechaCreacion, true)
    }));
    
    const ws = XLSX.utils.json_to_sheet(data);
    
    const wscols = [
      { wch: 8 },   // ID
      { wch: 25 },  // Nombre
      { wch: 50 },  // Descripción
      { wch: 8 },   // Orden
      { wch: 12 },  // Color
      { wch: 10 },  // Estado
      { wch: 20 }   // Fecha Creación
    ];
    ws['!cols'] = wscols;
    
    XLSX.utils.book_append_sheet(wb, ws, 'Estados de Recepción');
    
    const fecha = new Date();
    const fechaStr = fecha.toISOString().split('T')[0];
    const nombreArchivo = `${filename}_${fechaStr}.xlsx`;
    
    XLSX.writeFile(wb, nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: `Archivo ${nombreArchivo} generado exitosamente`,
      count: estados.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando a Excel:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar estados a PDF
 * @param {Array} estados - Lista de estados
 * @param {string} titulo - Título del reporte
 * @param {string} logoBase64 - Logo en base64 (opcional)
 * @returns {Object} Resultado de la exportación
 */
export const exportarPDF = (estados, titulo = 'Reporte de Estados de Recepción', logoBase64 = null) => {
  try {
    console.log('📊 Exportando PDF de estados de recepción...');
    
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (!estados || estados.length === 0) {
      throw new Error('No hay estados para exportar');
    }
    
    let jsPDF;
    try {
      const jspdf = require('jspdf');
      jsPDF = jspdf.jsPDF;
      require('jspdf-autotable');
    } catch (e) {
      console.error('❌ jsPDF no está disponible:', e);
      return {
        success: false,
        error: 'La librería jsPDF no está disponible',
        filename: null
      };
    }
    
    const doc = new jsPDF();
    const fecha = new Date();
    const fechaStr = fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    let y = 20;
    const marginLeft = 20;
    const pageWidth = doc.internal.pageSize.width;
    
    let logoAgregado = false;
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'JPEG', marginLeft, 10, 30, 15);
        logoAgregado = true;
        y = 35;
      } catch (logoError) {
        console.warn('⚠️ Error al agregar logo:', logoError);
        y = 20;
      }
    }
    
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const titleX = logoAgregado ? 60 : marginLeft;
    doc.text('JEAN INFORMATICO C.A.', titleX, y);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(titulo, titleX, y + 8);
    
    y += 20;
    doc.setFontSize(10);
    doc.text(`Fecha: ${fechaStr}`, marginLeft, y);
    doc.text(`Total: ${estados.length} estados`, marginLeft + 80, y);
    
    y += 15;
    const estadisticas = obtenerEstadisticasEstadosRecepcion(estados);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Resumen Estadístico', marginLeft, y);
    
    y += 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    const resumen = [
      `Estados activos: ${estadisticas.activos} (${estadisticas.porcentajeActivos}%)`,
      `Estados inactivos: ${estadisticas.inactivos} (${estadisticas.porcentajeInactivos}%)`,
      `Con descripción: ${estadisticas.conDescripcion} (${estadisticas.porcentajeConDescripcion}%)`,
      `Rango de órdenes: ${estadisticas.minOrden} - ${estadisticas.maxOrden}`,
      `Colores disponibles: ${Object.keys(estadisticas.colores).length}`
    ];
    
    resumen.forEach((linea, index) => {
      doc.text(`• ${linea}`, marginLeft + 5, y + (index * 5));
    });
    
    const tableData = estados.map(estado => [
      estado.idEstadoRecepcion || '',
      formatearNombre(estado.nombre),
      formatearDescripcion(estado.descripcion, 40) || '',
      estado.orden || 0,
      estado.color || '',
      estado.estado === 'activo' ? 'Activo' : 'Inactivo',
      formatearFecha(estado.fecha_creacion || estado.fechaCreacion)
    ]);
    
    doc.autoTable({
      startY: y + 25,
      head: [['ID', 'Nombre', 'Descripción', 'Orden', 'Color', 'Estado', 'Fecha']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [79, 70, 229],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9
      },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 30 },
        2: { cellWidth: 45 },
        3: { cellWidth: 10 },
        4: { cellWidth: 15 },
        5: { cellWidth: 12 },
        6: { cellWidth: 25 }
      },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak'
      },
      didDrawPage: function(data) {
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text('© Jean Informatico C.A. - Sistema de Gestión de Estados de Recepción', marginLeft, 290);
        doc.text(`Página ${data.pageNumber}`, pageWidth - marginLeft, 290, null, null, 'right');
      }
    });
    
    const fechaISO = fecha.toISOString().split('T')[0];
    const nombreArchivo = `estados_recepcion_${fechaISO}.pdf`;
    doc.save(nombreArchivo);
    
    return {
      success: true,
      filename: nombreArchivo,
      message: 'PDF generado exitosamente',
      count: estados.length,
      withLogo: logoAgregado
    };
    
  } catch (error) {
    console.error('❌ Error exportando PDF:', error);
    return {
      success: false,
      error: error.message,
      filename: null
    };
  }
};

/**
 * Exportar reporte completo (Excel + PDF)
 * @param {Array} estados - Lista de estados
 * @param {string} baseFilename - Nombre base del archivo
 * @param {string} logoBase64 - Logo en base64 (opcional)
 * @returns {Promise} Resultado de la exportación
 */
export const exportarReporteCompleto = async (estados, baseFilename = 'reporte_completo_estados_recepcion', logoBase64 = null) => {
  try {
    if (typeof window === 'undefined') {
      return {
        success: false,
        error: 'Exportación solo disponible en el navegador',
        filename: null
      };
    }
    
    if (estados.length === 0) {
      throw new Error('No hay datos para exportar');
    }
    
    const excelResult = exportarExcel(estados, baseFilename);
    if (!excelResult.success) {
      throw new Error(`Error en Excel: ${excelResult.error}`);
    }
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const pdfResult = exportarPDF(estados, `Reporte de Estados de Recepción - ${baseFilename}`, logoBase64);
    if (!pdfResult.success) {
      throw new Error(`Error en PDF: ${pdfResult.error}`);
    }
    
    return {
      success: true,
      excel: excelResult,
      pdf: pdfResult,
      message: 'Reporte completo generado exitosamente',
      count: estados.length
    };
    
  } catch (error) {
    console.error('❌ Error exportando reporte completo:', error);
    return {
      success: false,
      error: error.message,
      excel: null,
      pdf: null
    };
  }
};

// ==================== EXPORT DEFAULT ====================

export default {
  // Funciones API
  obtenerEstadosRecepcion,
  obtenerEstadosRecepcionOrdenados,
  obtenerEstadoRecepcionPorId,
  obtenerEstadoRecepcionConRelaciones,
  verificarEstadoRecepcionPorNombre,
  verificarEstadoRecepcionPorId,
  crearEstadoRecepcion,
  actualizarEstadoRecepcion,
  eliminarEstadoRecepcion,
  activarEstadoRecepcion,
  desactivarEstadoRecepcion,
  cambiarEstadoActivo,
  ordenarEstadosRecepcion,
  
  // Funciones auxiliares
  formatearFecha,
  formatearNombre,
  formatearDescripcion,
  buscarEstadosLocal,
  filtrarEstadosPorEstadoLocal,
  obtenerEstadisticasEstadosRecepcion,
  ordenarEstados,
  obtenerClaseColor,
  obtenerEtiquetaEstado,
  obtenerSiguienteEstado,
  obtenerEstadoAnterior,
  
  // Funciones de exportación
  exportarExcel,
  exportarPDF,
  exportarReporteCompleto
};